/**
 * CookieConsentBanner.tsx — GDPR Cookie Consent (FASE 12, Luglio 2026)
 *
 * Banner informativo sui cookie conforme al GDPR.
 * Mostrato al primo accesso. Le preferenze vengono salvate in localStorage.
 *
 * USAGE:
 *   <CookieConsentBanner />
 *   // Mettilo in App.tsx o nel layout principale
 */

import React, { useEffect, useState } from "react";
import { Shield, X } from "lucide-react";

const STORAGE_KEY = "rc-cookie-consent";
const CONSENT_VERSION = 1;

interface ConsentData {
  version: number;
  accepted: boolean;
  timestamp: number;
}

function getStoredConsent(): ConsentData | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw) as ConsentData;
    if (data.version !== CONSENT_VERSION) return null; // Invalida se aggiornato
    return data;
  } catch {
    return null;
  }
}

function storeConsent(accepted: boolean): void {
  const data: ConsentData = {
    version: CONSENT_VERSION,
    accepted,
    timestamp: Date.now(),
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

export default function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);
  const [preferences, setPreferences] = useState<{
    necessary: boolean;
    analytics: boolean;
    marketing: boolean;
  }>({ necessary: true, analytics: false, marketing: false });

  useEffect(() => {
    const stored = getStoredConsent();
    if (!stored) {
      setVisible(true);
    }
  }, []);

  const handleAcceptAll = () => {
    storeConsent(true);
    setPreferences({ necessary: true, analytics: true, marketing: true });
    setVisible(false);
  };

  const handleAcceptNecessary = () => {
    storeConsent(false);
    setPreferences({ necessary: true, analytics: false, marketing: false });
    setVisible(false);
  };

  const handleSavePreferences = () => {
    storeConsent(preferences.analytics || preferences.marketing);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    /* FIX SAFE-AREA (02 Set 2026): il pulsante "Accetta tutti" finiva
       sotto la gesture bar di sistema e non era cliccabile. Padding
       inferiore = 1rem + inset di sistema. */
    <div
      role="dialog"
      aria-labelledby="cookie-consent-title"
      className="fixed bottom-0 left-0 right-0 z-[9999] p-4 md:p-6 bg-carbon-950/95 backdrop-blur-xl border-t border-white/[0.12] shadow-2xl animate-slide-up"
      style={{
        paddingBottom:
          "calc(1rem + env(safe-area-inset-bottom, var(--safe-area-inset-bottom, 0px)))",
      }}
    >
      <div className="max-w-3xl mx-auto">
        <div className="flex items-start gap-3 mb-3">
          <Shield className="w-5 h-5 text-brand-400 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <h3
              id="cookie-consent-title"
              className="text-sm font-semibold text-white"
            >
              🍪 Cookie &amp; Privacy
            </h3>
            <p className="text-xs text-neutral-400 mt-1 leading-relaxed">
              RideConnect usa cookie tecnici necessari al funzionamento. Con il
              tuo consenso, possiamo raccogliere statistiche anonime d'uso
              (funnel interno, nessun servizio esterno) e usare cookie di
              marketing per mostrarti eventi nella tua zona. Nessun dato viene
              venduto a terzi.{" "}
              <a
                href="/privacy"
                target="_blank"
                className="text-brand-400 underline hover:text-brand-300"
              >
                Privacy Policy
              </a>
            </p>

            {/* Toggle preferenze */}
            <div className="mt-3 space-y-2">
              <label className="flex items-center gap-2 text-xs text-neutral-300 cursor-not-allowed opacity-70">
                <input
                  type="checkbox"
                  checked={preferences.necessary}
                  disabled
                  className="accent-brand-500"
                />
                Necessari (sempre attivi) — autenticazione, sicurezza
              </label>
              <label className="flex items-center gap-2 text-xs text-neutral-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={preferences.analytics}
                  onChange={(e) =>
                    setPreferences((p) => ({
                      ...p,
                      analytics: e.target.checked,
                    }))
                  }
                  className="accent-brand-500"
                />
                Analitici — statistiche anonime d'uso (nessun servizio esterno)
              </label>
              <label className="flex items-center gap-2 text-xs text-neutral-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={preferences.marketing}
                  onChange={(e) =>
                    setPreferences((p) => ({
                      ...p,
                      marketing: e.target.checked,
                    }))
                  }
                  className="accent-brand-500"
                />
                Marketing — eventi locali, chapter suggeriti
              </label>
            </div>
          </div>

          <button
            onClick={() => setVisible(false)}
            className="text-neutral-500 hover:text-white transition-colors p-1"
            aria-label="Chiudi banner cookie"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Bottoni azione */}
        <div className="flex flex-wrap gap-2 justify-end">
          <button
            onClick={handleAcceptNecessary}
            className="px-3 py-1.5 text-xs rounded-lg border border-white/[0.15] text-neutral-300 hover:bg-white/[0.05] transition-colors"
          >
            Solo necessari
          </button>
          <button
            onClick={handleSavePreferences}
            className="px-3 py-1.5 text-xs rounded-lg border border-white/[0.15] text-neutral-300 hover:bg-white/[0.05] transition-colors"
          >
            Salva preferenze
          </button>
          <button
            onClick={handleAcceptAll}
            className="px-4 py-1.5 text-xs rounded-lg bg-brand-500 text-white font-semibold hover:bg-brand-600 transition-colors"
          >
            Accetta tutti
          </button>
        </div>
      </div>
    </div>
  );
}

/** Hook per verificare se l'utente ha dato il consenso */
export function useCookieConsent(): { analytics: boolean; marketing: boolean } {
  const stored = getStoredConsent();
  if (!stored) return { analytics: false, marketing: false };
  if (stored.accepted) return { analytics: true, marketing: true };
  return { analytics: false, marketing: false };
}
